//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { FulfillmentDetails } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { NO_INFORMATION_PROVIDED } from '@campaigns/constants/DefaultEmptyValuesConstants';
import {
  BOX_OPTIONS,
  getPackageInsertOptions,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import {
  BodyBoldText,
  BodyText,
  SubsectionText,
} from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';

interface PackagingSectionProps {
  bvClientId: string | undefined;
  comment: string | undefined;
  details: FulfillmentDetails | undefined;
}

export const renderPackageWeight = (
  packageWeight: FulfillmentDetails['packageWeight'],
) => {
  if (!packageWeight) {
    return undefined;
  }

  const { kg, lb, oz, unit } = packageWeight;

  if (unit === 'metric' && isDefined(kg)) {
    return `${kg} kg`;
  }

  if (unit === 'imperial') {
    const lbs = isDefined(lb) ? `${lb} lbs` : '';
    const ozs = isDefined(oz) ? `${oz} oz` : '';
    const weight = [lbs, ozs].filter(Boolean).join(' ');
    return weight || undefined;
  }

  return undefined;
};

export const PackagingSection = ({
  bvClientId,
  comment,
  details,
}: PackagingSectionProps) => {
  const packageInsertOptions = getPackageInsertOptions(bvClientId);
  const packageWeight = renderPackageWeight(details?.packageWeight);

  if (
    !isDefined(details?.boxType) &&
    !packageWeight &&
    !isDefined(details?.participantCount) &&
    !isDefined(comment)
  ) {
    return (
      <Section>
        <SubsectionText>Packaging</SubsectionText>
        <BodyText>{NO_INFORMATION_PROVIDED}</BodyText>
      </Section>
    );
  }

  return (
    <Section>
      <SubsectionText>Packaging</SubsectionText>
      {isDefined(details?.boxType) && (
        <DetailsItem>
          <BodyText>Package Type</BodyText>
          <BodyText>{BOX_OPTIONS[details.boxType].label}</BodyText>
        </DetailsItem>
      )}
      {isDefined(details?.packageInsert) && (
        <DetailsItem>
          <BodyText>Package Inserts</BodyText>
          <BodyText>
            {packageInsertOptions[details.packageInsert].label}
          </BodyText>
        </DetailsItem>
      )}
      {packageWeight && (
        <DetailsItem>
          <BodyText>Package Weight</BodyText>
          <BodyText>{packageWeight}</BodyText>
        </DetailsItem>
      )}
      {isDefined(details?.participantCount) && (
        <DetailsItem>
          <BodyText>Participant Count</BodyText>
          <BodyText>{details.participantCount}</BodyText>
        </DetailsItem>
      )}
      {isDefined(comment) && (
        <PackagingDetailsItem>
          <BodyBoldText>Packaging Instructions</BodyBoldText>
          <BodyText whiteSpace='pre-line'>{comment}</BodyText>
        </PackagingDetailsItem>
      )}
    </Section>
  );
};

const Section = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const DetailsItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const PackagingDetailsItem = styled(DetailsItem)`
  gap: ${({ theme }) => theme.spacings[3]};
`;
